import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { computed, effect, signal } from '@preact/signals-core';
import { css } from 'lit';
import { property } from 'lit/decorators.js';

import {
  type CellRenderProps,
  type DataViewCellLifeCycle,
  renderUniLit,
} from '../../../core/index.js';
import {
  dataViewCellContainerStyles,
  shouldExpandDataViewCell,
} from '../../../core/view/cell-container.js';
import {
  TableViewAreaSelection,
  type TableViewSelectionWithType,
} from '../selection';
import type { TableProperty, TableSingleView } from '../table-view-manager.js';
import type { MobileTableViewUILogic } from './table-view-ui-logic.js';

export class MobileTableCell extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = css`
    ${dataViewCellContainerStyles('mobile-table-cell')}
  `;

  private readonly _cell = signal<DataViewCellLifeCycle>();

  @property({ attribute: false })
  accessor column!: TableProperty;

  @property({ attribute: false })
  accessor rowId!: string;

  cell$ = computed(() => {
    return this.column.cellGetOrCreate(this.rowId);
  });

  isSelectionEditing$ = computed(() => {
    const selection = this.tableViewLogic.selection$.value;
    if (selection?.selectionType !== 'area') {
      return false;
    }
    if (selection.groupKey !== this.groupKey) {
      return false;
    }
    if (selection.focus.columnIndex !== this.columnIndex) {
      return false;
    }
    if (selection.focus.rowIndex !== this.rowIndex) {
      return false;
    }
    return selection.isEditing;
  });

  selectCurrentCell = (editing: boolean) => {
    if (this.view.readonly$.value) {
      return;
    }
    const setSelection = this.tableViewLogic.setSelection.bind(
      this.tableViewLogic
    );
    const viewId = this.tableViewLogic.view.id;
    if (setSelection && viewId) {
      if (editing && this.cell?.beforeEnterEditMode() === false) {
        return;
      }
      setSelection({
        viewId,
        type: 'table',
        ...TableViewAreaSelection.create({
          groupKey: this.groupKey,
          focus: {
            rowIndex: this.rowIndex,
            columnIndex: this.columnIndex,
          },
          isEditing: editing,
        }),
      });
    }
  };

  get cell(): DataViewCellLifeCycle | undefined {
    return this._cell.value;
  }

  private get groupKey() {
    return this.closest('mobile-table-group')?.group?.key;
  }

  override connectedCallback() {
    super.connectedCallback();
    if (this.column.valueReadonly$.value) return;
    this.disposables.add(
      effect(() => {
        // `afterEnterEditingMode` / `beforeExitEditingMode` fire from
        // BaseCellRenderer when `isEditing$` flips; mobile-table only
        // mirrors the selection-driven signal onto the container.
        const isEditing = this.isSelectionEditing$.value;
        if (isEditing && !this.isEditing$.peek()) {
          this.isEditing$.value = true;
        } else if (!isEditing && this.isEditing$.peek()) {
          this.isEditing$.value = false;
        }
      })
    );
    this.disposables.addFromEvent(this, 'click', () => {
      if (!this.isEditing$.value) {
        this.selectCurrentCell(!this.column.valueReadonly$.value);
      }
    });

    // Effect for cell display mode (expanded, collapsed, or smart)
    this.disposables.add(
      effect(() => {
        const tableView = this.view as TableSingleView;
        const mode = tableView.cellDisplayMode$.value;

        const selection = this.tableViewLogic.selection$.value;
        const expanded = shouldExpandDataViewCell({
          isEditing: this.isEditing$.value,
          mode,
          rowSelected: this.isRowSelected(selection),
        });
        this.dataset['expanded'] = `${expanded}`;
      })
    );
  }

  /**
   * Check if this row is currently selected (for smart expand mode)
   */
  private isRowSelected(
    selection: TableViewSelectionWithType | undefined
  ): boolean {
    if (!selection) return false;

    if (selection.selectionType === 'area') {
      // Area selection: check if focus row matches
      if (selection.groupKey !== this.groupKey) return false;
      return selection.focus.rowIndex === this.rowIndex;
    } else if (selection.selectionType === 'row') {
      // Row selection: check if this row is in the selected rows
      return selection.rows.some(
        row => row.id === this.rowId && row.groupKey === this.groupKey
      );
    }
    return false;
  }

  override render() {
    const renderer = this.column.renderer$.value;
    if (!renderer) {
      return;
    }
    const { view } = renderer;
    this.view.lockRows(this.isEditing$.value);
    this.dataset['editing'] = `${this.isEditing$.value}`;
    const props: CellRenderProps = {
      cell: this.cell$.value,
      isEditing$: this.isEditing$,
      selectCurrentCell: this.selectCurrentCell,
    };

    return renderUniLit(view, props, {
      ref: this._cell,
      style: {
        display: 'contents',
      },
    });
  }

  @property({ attribute: false })
  accessor columnId!: string;

  @property({ attribute: false })
  accessor columnIndex!: number;

  isEditing$ = signal(false);

  @property({ attribute: false })
  accessor rowIndex!: number;

  @property({ attribute: false })
  accessor tableViewLogic!: MobileTableViewUILogic;

  get view() {
    return this.tableViewLogic.view;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mobile-table-cell': MobileTableCell;
  }
}
